import React, { useMemo } from 'react';
import { Button } from 'antd';
import {
  CloseOutlined,
  CheckCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { getSignedCvUrl } from '../../data/analysis';
import { Candidate } from '../../data/candidate';
import AvailabilityHeatMap from '../availability/AvailabilityHeatMap';
import { buildAnonymizedOriginalCvLabel, getExtension } from '../../utils/cvFileLabels';

interface CandidateCardProps {
  activeCandidate: Candidate | null;
  modalVisible: boolean;
  closeCandidateModal: () => void;
  getMatchScore: (candidateId: string) => number;
  staticCandidates: any;
}

interface CategorizedCVs {
  original: string[];
  generated: string[];
}

const CandidateCard: React.FC<CandidateCardProps> = ({
  activeCandidate,
  modalVisible,
  closeCandidateModal,
  getMatchScore,
  staticCandidates
}) => {
  const categorizedCVs = useMemo(() => {
    if (!activeCandidate?.s3_keys?.length) return { original: [], generated: [] };
    const filteredKeys = activeCandidate.s3_keys.filter(key => {
      const fileName = key.toLowerCase();
      return !fileName.endsWith('.tex');
    });
    return filteredKeys.reduce<CategorizedCVs>((acc, key) => {
      const isGenerated = key.toLowerCase().includes('export_');
      
      if (isGenerated) {
        acc.generated.push(key);
      } else {
        acc.original.push(key);
      }
      return acc;
    }, { original: [], generated: [] });
  }, [activeCandidate?.s3_keys]);
  
  if (!activeCandidate) return null;

  return (
    <div className={`company-modal-overlay ${modalVisible ? 'active' : ''}`} onClick={closeCandidateModal}>
      {activeCandidate && (
        <div className="company-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">{activeCandidate.cv?.full_name || activeCandidate.name}</h3>
            <button className="modal-close" onClick={closeCandidateModal}>
              <CloseOutlined />
            </button>
          </div>

          <div className="modal-content"> {/* This div is scrollable */}
            {/* --- Stats & Match Reason --- */}
            <div className="modal-stats">
              <div className="stat-item">
                <div className="stat-label">Match Score</div>
                <div className="stat-value highlighted">{Math.round(getMatchScore(activeCandidate._id) * 100)}%</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">Experience</div>
                <div className="stat-value">
                  {(() => {
                    const startYear = activeCandidate.cv?.personal_info?.in_industry_since;
                    const expYears = startYear && startYear > 0 && startYear <= new Date().getFullYear()
                                      ? new Date().getFullYear() - startYear 
                                      : null;
                    return expYears !== null ? `${expYears} years` : 'N/A';
                  })()}
                </div>
              </div>
            </div>

            {staticCandidates.reasons_include && activeCandidate._id && staticCandidates.reasons_include[activeCandidate._id as keyof typeof staticCandidates.reasons_include] && (
              <div className="modal-section">
                <h4 className="section-title">Match Reason</h4>
                <p className="description-text">
                  {String(staticCandidates.reasons_include[activeCandidate._id as keyof typeof staticCandidates.reasons_include]).replace(/^Score [\d.]+: /, '')}
                </p>
              </div>
            )}

            {activeCandidate?.s3_keys?.length > 0 && (
              <div className="cv-container-horizontal">
                <div className="cv-section cv-section-half">
                  <h4 className="cv-category-title">Original CVs ({categorizedCVs.original.length})</h4>
                  <div className="cv-scroll-container">
                    {categorizedCVs.original.map((key, index) => {
                      const fileName = key.split('/').pop();
                      const displayName = buildAnonymizedOriginalCvLabel(fileName, index);
                      return (
                        <button
                          key={`original-${index}`}
                          className="cv-button"
                          onClick={async () => {
                            const url = await getSignedCvUrl(activeCandidate._id, key);
                            window.open(url, '_blank');
                          }}
                          title={displayName}
                        >
                          <DownloadOutlined className="cv-icon" />
                          <span className="cv-filename">{displayName}</span>
                        </button>
                      );
                    })}
                    {categorizedCVs.original.length === 0 && (
                      <span className="empty-notice">No original CVs</span>
                    )}
                  </div>
                </div>
                
                <div className="cv-divider"></div>
                
                <div className="cv-section cv-section-half">
                  <h4 className="cv-category-title">Generated CVs ({categorizedCVs.generated.length})</h4>
                  <div className="cv-scroll-container">
                    {categorizedCVs.generated.map((key, index) => {
                      const fileName = key.split('/').pop();
                      const extension = getExtension(fileName);
                      const displayName = fileName?.startsWith('export_')
                        ? `Generated_CV${extension}`
                        : fileName;
                      return (
                        <button
                          key={`generated-${index}`}
                          className="cv-button cv-button-generated"
                          onClick={async () => {
                            const url = await getSignedCvUrl(activeCandidate._id, key);
                            window.open(url, '_blank');
                          }}
                          title={fileName}
                        >
                          <DownloadOutlined className="cv-icon" />
                          <span className="cv-filename">{displayName}</span>
                        </button>
                      );
                    })}
                    {categorizedCVs.generated.length === 0 && (
                      <span className="empty-notice">No generated CVs</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeCandidate.planned_available_capacity && (
            <div className="modal-section">
                <h4 className="section-title">Availability</h4>
                <AvailabilityHeatMap availabilityData={activeCandidate.planned_available_capacity} />
            </div>
            )}

            {/* --- Professional Profile --- */}
            <div className="modal-section">
              <h4 className="section-title">Professional Profile</h4>
              {activeCandidate.cv?.personal_info?.position && (
                <div className="info-block">
                  <span className="info-block-label">Current Position:</span>
                  <span className="info-block-value">{activeCandidate.cv.personal_info.position}</span>
                </div>
              )}
              {activeCandidate.cv?.personal_info?.competence && activeCandidate.cv.personal_info.competence !== "Err:509" && activeCandidate.cv.personal_info.competence !== "Missing data" && (
                <div className="info-block">
                  <span className="info-block-label">Key Competencies:</span>
                  <span className="info-block-value">{activeCandidate.cv.personal_info.competence}</span>
                </div>
              )}
               {activeCandidate.cv?.personal_info?.employed_since > 0 && (
                <div className="info-block">
                  <span className="info-block-label">Employed Since:</span>
                  <span className="info-block-value">{activeCandidate.cv.personal_info.employed_since}</span>
                </div>
              )}
            </div>
            
            {/* --- Skills --- */}
            {activeCandidate.cv?.skills && activeCandidate.cv.skills.length > 0 && 
             (activeCandidate.cv.skills[0]?.category !== "Missing data" || activeCandidate.cv.skills.length > 1) && (
              <div className="modal-section">
                <h4 className="section-title">Skills</h4>
                <div className="offering-tags modal-offering-tags">
                  {activeCandidate.cv.skills.map((skill, idx) => (
                    skill.category !== "Missing data" && (
                      <span key={`modal-skill-${idx}`} className="modal-offering-tag matched">
                        <CheckCircleOutlined style={{ marginRight: '5px', verticalAlign: 'middle' }} /> 
                        {skill.category}
                        {skill.description && skill.description !== skill.category && skill.description !== "Missing data" && (
                          <span className="skill-description-tooltip"> ({skill.description})</span>
                        )}
                      </span>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* --- Contact & Location --- */}
            <div className="modal-section">
              <h4 className="section-title">Contact & General Info</h4>
              {activeCandidate.cv?.email && (
                <div className="info-block">
                  <span className="info-block-label">Email:</span>
                  <span className="info-block-value">{activeCandidate.cv.email}</span>
                </div>
              )}
              {activeCandidate.cv?.phone && (
                <div className="info-block">
                  <span className="info-block-label">Phone:</span>
                  <span className="info-block-value">{activeCandidate.cv.phone}</span>
                </div>
              )}
               {activeCandidate.cv?.address && (
                <div className="info-block">
                  <span className="info-block-label">Address:</span>
                  <span className="info-block-value">{activeCandidate.cv.address}</span>
                </div>
              )}
              {activeCandidate.cv?.personal_info?.birth_year > 0 && (
                <div className="info-block">
                  <span className="info-block-label">Birth Year:</span>
                  <span className="info-block-value">{activeCandidate.cv.personal_info.birth_year}</span>
                </div>
              )}
              {activeCandidate.cv?.personal_info?.residence && (activeCandidate.cv.personal_info.residence !== "Missing data") &&(
                <div className="info-block">
                  <span className="info-block-label">Residence:</span>
                  <span className="info-block-value">{activeCandidate.cv.personal_info.residence}</span>
                </div>
              )}
              {activeCandidate.cv?.personal_info?.nationality && (activeCandidate.cv.personal_info.nationality !== "Missing data") && (
                <div className="info-block">
                  <span className="info-block-label">Nationality:</span>
                  <span className="info-block-value">{activeCandidate.cv.personal_info.nationality}</span>
                </div>
              )}
            </div>

            {/* --- Personal Qualities --- */}
            {((activeCandidate.cv?.personal_description?.characteristics && activeCandidate.cv.personal_description.characteristics !== "Missing data") ||
              (activeCandidate.cv?.personal_description?.motivated_by && (activeCandidate.cv.personal_description.motivated_by !== "Missing data")) ||
              (activeCandidate.cv?.personal_description?.hobbies && (activeCandidate.cv.personal_description.hobbies !== "Missing data"))) && (
              <div className="modal-section">
                <h4 className="section-title">Personal Qualities</h4>
                {activeCandidate.cv?.personal_description?.characteristics && activeCandidate.cv.personal_description.characteristics !== "Missing data" && (
                  <div className="info-block">
                    <span className="info-block-label">Characteristics:</span>
                    <span className="info-block-value">{activeCandidate.cv.personal_description.characteristics}</span>
                  </div>
                )}
                {activeCandidate.cv?.personal_description?.motivated_by && activeCandidate.cv.personal_description.motivated_by !== "Missing data" && (
                  <div className="info-block">
                    <span className="info-block-label">Motivated By:</span>
                    <span className="info-block-value">{activeCandidate.cv.personal_description.motivated_by}</span>
                  </div>
                )}
                 {activeCandidate.cv?.personal_description?.hobbies && activeCandidate.cv.personal_description.hobbies !== "Missing data" && (
                  <div className="info-block">
                    <span className="info-block-label">Hobbies:</span>
                    <span className="info-block-value">{activeCandidate.cv.personal_description.hobbies}</span>
                  </div>
                )}
              </div>
            )}
            
            {/* --- Reference Assignments --- */}
            {activeCandidate.cv?.reference_assignments && activeCandidate.cv.reference_assignments.length > 0 &&
             (activeCandidate.cv.reference_assignments[0]?.description !== "Missing data" || activeCandidate.cv.reference_assignments.length > 1) && (
              <div className="modal-section">
                <h4 className="section-title">Reference Assignments</h4>
                {activeCandidate.cv.reference_assignments.map((ref, index) => (
                  ref.description !== "Missing data" && (
                    <div key={`ref-${index}`} className="list-item-card">
                      {ref.period && <p className="list-item-period">{ref.period}</p>}
                      {ref.organization && <p className="list-item-title">{ref.organization}</p>}
                      {ref.description && <p className="list-item-description">{ref.description}</p>}
                    </div>
                  )
                ))}
              </div>
            )}

            {/* --- Previous Employment --- */}
            {activeCandidate.cv?.previous_employment && activeCandidate.cv.previous_employment.length > 0 && (
              <div className="modal-section">
                <h4 className="section-title">Previous Employment</h4>
                {activeCandidate.cv.previous_employment.map((emp, index) => (
                  <div key={`emp-${index}`} className="list-item-card">
                    {emp.period && <p className="list-item-period">{emp.period}</p>}
                    {emp.company && <p className="list-item-title">{emp.company}</p>}
                    {emp.position && <p className="list-item-subtitle">{emp.position}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* --- Education & Merits --- */}
            {activeCandidate.cv?.education_merits && activeCandidate.cv.education_merits.length > 0 && (
              <div className="modal-section">
                <h4 className="section-title">Further Education & Merits</h4>
                {activeCandidate.cv.education_merits.map((edu, index) => (
                  <div key={`edu-merit-${index}`} className="list-item-card">
                    {edu.year && <p className="list-item-period">{edu.year}</p>}
                    {edu.title && <p className="list-item-title">{edu.title}</p>}
                    {edu.institution && <p className="list-item-subtitle">{edu.institution}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* --- Basic Education --- */}
            {activeCandidate.cv?.basic_education && activeCandidate.cv.basic_education.length > 0 && (
              <div className="modal-section">
                <h4 className="section-title">Basic Education</h4>
                {activeCandidate.cv.basic_education.map((edu, index) => (
                  <div key={`edu-basic-${index}`} className="list-item-card">
                    {edu.year && <p className="list-item-period">{edu.year}</p>}
                    {edu.title && <p className="list-item-title">{edu.title}</p>}
                    {edu.institution && <p className="list-item-subtitle">{edu.institution}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <Button type="primary" className="button-primary" onClick={closeCandidateModal}>Close</Button>
          </div>
        </div>
      )}
    </div> 
  );
};

export default CandidateCard;
